import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { timeAlertsFromError, alertLines } from '@/features/time-tracking/time-alerts';
import { useActiveTimer, ACTIVE_TIMER_KEY } from '@/features/time-tracking/api';
import { switchHandoffStart, stoppedElsewhereFrom, stoppedElsewhereMessage, type StoppedElsewhere } from '@merkos-mgmt/shared';
import type { TimeEntry } from '@merkos-mgmt/shared';

export interface StopTimerOptions {
  /** Explicit end (ISO). Omitted → the server stamps now. Lets the user shorten an entry that breaks a time rule. */
  endTime?: string;
  /** The user has seen the alert-only rules this stop trips and wants to go ahead. */
  acknowledged?: boolean;
  /**
   * The entry the user means to stop (e.g. the one a stop sheet was opened for).
   * If the running entry is no longer that one — it was stopped elsewhere and a
   * new one started — nothing is stopped and {@link TimerStoppedElsewhere} is
   * thrown, so a stale screen can never close the replacement entry.
   */
  entryId?: string;
}

/**
 * The running entry turned out to be closed already — stopped from another tab
 * or device (or deleted) while this screen still showed it. The provider has
 * cleared its stale timer before throwing; callers show `message` as
 * information, not as a failure.
 */
export class TimerStoppedElsewhere extends Error {
  constructor(public readonly info: StoppedElsewhere) {
    super(stoppedElsewhereMessage(info));
    this.name = 'TimerStoppedElsewhere';
  }
}

interface TimerState {
  activeEntry: TimeEntry | null;
  elapsed: number;
  isPending: boolean;
  start: (projectId: string) => Promise<void>;
  /**
   * Close the running entry. Throws the server's error unchanged — a 409 that
   * trips time rules is read with `timeAlertsFromError` so the caller can show
   * the reasons and retry with a shorter `endTime` or `acknowledged` — except
   * that an entry already closed elsewhere throws {@link TimerStoppedElsewhere}.
   */
  stop: (options?: StopTimerOptions) => Promise<void>;
  /**
   * Close the running entry and open a new one on `projectId` (a no-op if it is
   * the current project). The new entry starts just after the old one ended so
   * the handoff is continuous and clears the server's overlap boundary. Resolves
   * `true` when the switch happened, `false` when the user cancelled an alert
   * confirmation (nothing changed); throws on a hard block or request failure,
   * and {@link TimerStoppedElsewhere} (nothing started) when the old entry was
   * already closed elsewhere.
   */
  switchProject: (projectId: string) => Promise<boolean>;
}

const TimerContext = createContext<TimerState | undefined>(undefined);

export function TimerProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  // The running entry is the polled query (refetched every minute and whenever
  // the app comes back to the foreground), so a timer stopped on another device
  // or tab clears here too instead of staying on screen until a reload.
  const { data } = useActiveTimer();
  const activeEntry = data ?? null;
  const setActiveEntry = useCallback((entry: TimeEntry | null) => {
    qc.setQueryData(ACTIVE_TIMER_KEY, entry);
  }, [qc]);
  const [elapsed, setElapsed] = useState(0);
  const [isPending, setIsPending] = useState(false);

  // Tick every second when active
  useEffect(() => {
    if (!activeEntry?.start_time) {
      setElapsed(0);
      return;
    }
    const startMs = new Date(activeEntry.start_time).getTime();
    const tick = () => setElapsed(Math.floor((Date.now() - startMs) / 1000));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [activeEntry?.start_time]);

  /** A stop found the entry already closed: drop the stale timer, refresh, and say so. */
  const stoppedElsewhereError = useCallback((err: unknown): TimerStoppedElsewhere | null => {
    const info = err instanceof ApiError ? stoppedElsewhereFrom(err.status, err.data) : null;
    if (!info) return null;
    setActiveEntry(null);
    qc.invalidateQueries({ queryKey: ['mobile-time-entries'] });
    qc.invalidateQueries({ queryKey: ACTIVE_TIMER_KEY });
    return new TimerStoppedElsewhere(info);
  }, [qc, setActiveEntry]);

  const start = useCallback(async (projectId: string) => {
    setIsPending(true);
    try {
      const entry = await api<TimeEntry>('/time-entries', {
        method: 'POST',
        body: JSON.stringify({ project_id: projectId, start_time: new Date().toISOString() }),
      });
      setActiveEntry(entry);
      setElapsed(0);
      qc.invalidateQueries({ queryKey: ['mobile-time-entries'] });
      qc.invalidateQueries({ queryKey: ACTIVE_TIMER_KEY });
    } finally {
      setIsPending(false);
    }
  }, [qc, setActiveEntry]);

  const stop = useCallback(async (options: StopTimerOptions = {}) => {
    if (options.entryId && options.entryId !== activeEntry?.id) {
      throw new TimerStoppedElsewhere({ endTime: null, deleted: false });
    }
    if (!activeEntry) return;
    setIsPending(true);
    try {
      // Send the device timezone so the backend evaluates time rules and clamps
      // the stop against the user's local day, not UTC.
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      try {
        await api<TimeEntry>(`/time-entries/${activeEntry.id}/stop`, {
          method: 'POST',
          body: JSON.stringify({ timezone, end_time: options.endTime, acknowledged: options.acknowledged ?? false }),
        });
      } catch (err) {
        throw stoppedElsewhereError(err) ?? err;
      }
      setActiveEntry(null);
      qc.invalidateQueries({ queryKey: ['mobile-time-entries'] });
      qc.invalidateQueries({ queryKey: ACTIVE_TIMER_KEY });
    } finally {
      setIsPending(false);
    }
  }, [activeEntry, qc, setActiveEntry, stoppedElsewhereError]);

  const switchProject = useCallback(async (projectId: string): Promise<boolean> => {
    if (!activeEntry || projectId === activeEntry.project_id) return false;
    setIsPending(true);
    // Send the device timezone so the backend validates project dates and clamps
    // the stop against the user's local day, not UTC.
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const stopEntry = (acknowledged: boolean) =>
      api<TimeEntry>(`/time-entries/${activeEntry.id}/stop`, {
        method: 'POST',
        body: JSON.stringify({ timezone, acknowledged }),
      });
    let stoppedEnd: string | null | undefined;
    try {
      // Stopping may trip time-alert rules. A hard block can't be overridden; an
      // alert-only warning can, so surface it and let the user confirm — mobile
      // has no dedicated alert dialog, so a confirm stands in for the web gate.
      let stopped: TimeEntry;
      try {
        stopped = await stopEntry(false);
      } catch (err) {
        const alerts = timeAlertsFromError(err);
        if (alerts) {
          if (alerts.blocked.length > 0) {
            throw new Error(`Can't switch — a time rule blocks stopping this entry: ${alertLines(alerts.blocked)}. Tap Stop to change its end time.`);
          }
          const lines = alerts.warnings.map((a) => `• ${a.message || a.ruleName}`).join('\n');
          if (!window.confirm(`${lines || 'This entry trips a time rule.'}\n\nStop it and switch project anyway?`)) {
            return false; // user cancelled — nothing changed, old timer still running
          }
          try {
            stopped = await stopEntry(true);
          } catch (retryErr) {
            // Stopped elsewhere while the user read the warning.
            throw stoppedElsewhereError(retryErr) ?? retryErr;
          }
        } else {
          throw stoppedElsewhereError(err) ?? err;
        }
      }
      stoppedEnd = stopped.end_time;
      // Start the new entry just after the old one ended (see switchHandoffStart).
      const entry = await api<TimeEntry>('/time-entries', {
        method: 'POST',
        body: JSON.stringify({ project_id: projectId, start_time: switchHandoffStart(stoppedEnd), timezone }),
      });
      setActiveEntry(entry);
      qc.invalidateQueries({ queryKey: ['mobile-time-entries'] });
      qc.invalidateQueries({ queryKey: ACTIVE_TIMER_KEY });
      return true;
    } catch (err) {
      // The old entry was already stopped but the new one didn't start — reflect
      // the stopped state so the UI doesn't keep showing a running timer.
      if (stoppedEnd !== undefined) {
        setActiveEntry(null);
        qc.invalidateQueries({ queryKey: ['mobile-time-entries'] });
      }
      throw err;
    } finally {
      setIsPending(false);
    }
  }, [activeEntry, qc, setActiveEntry, stoppedElsewhereError]);

  return (
    <TimerContext.Provider value={{ activeEntry, elapsed, isPending, start, stop, switchProject }}>
      {children}
    </TimerContext.Provider>
  );
}

export function useTimer() {
  const ctx = useContext(TimerContext);
  if (!ctx) throw new Error('useTimer must be used within TimerProvider');
  return ctx;
}
